<template>
  <ReflectModal
    :title-message="$t('settings.dataQuality.title')"
    @cancel="closeModal()"
  >
    <div class="container">
      <div
        v-if="getObjectKeys(dataCompletenessDetail).length > 0"
        class="container__section"
      >
        <SettingsDataHealthModalSection
          :data-health-detail="dataCompletenessDetail"
          :data-health-type="DataHealthCheckType.NULL_VALUES"
          :date-range="dateRange"
        />
      </div>
      <div
        v-if="getObjectKeys(dataCoherenceDetail).length > 0"
        class="container__section"
      >
        <SettingsDataHealthModalSection
          :data-health-detail="dataCoherenceDetail"
          :data-health-type="DataHealthCheckType.INCOHERENT_VALUES"
          :date-range="dateRange"
        />
      </div>
      <div
        v-if="getObjectKeys(dataQualityDetail.normalizationChecks).length > 0"
        class="container__section"
      >
        <p class="section__title">
          <i class="title__icon icon-normalization" />
          {{ $t('settings.dataQuality.dataNormalization') }}
        </p>
        <div
          v-for="(status, field) in dataQualityDetail.normalizationChecks"
          :key="field"
          class="section__field-container"
          @click="navigateToNormalization(field)"
        >
          <p class="field-container__info field-container__name">
            {{
              $t(
                `settings.dataNormalization.module.normalizationFields.${field}`
              )
            }}
          </p>
          <p class="field-container__info field-container__status">
            <Loader
              v-if="!status || status === NormalizationCheckStatus.LOADING"
              class="field-container__status__loader"
            />
            <template v-else>
              <i
                :class="[
                  'field-container__status__icon',
                  status === NormalizationCheckStatus.SUCCESS
                    ? ['icon-success', 'status-icon__success']
                    : ['icon-error', 'status-icon__error']
                ]"
              />
              {{
                status === NormalizationCheckStatus.SUCCESS
                  ? $t(
                      'settings.dataNormalization.module.infoModal.completeNormalization'
                    )
                  : $t(
                      'settings.dataNormalization.module.infoModal.incompleteNormalization'
                    )
              }}
            </template>
          </p>
          <p class="field-container__info field-container__open-details">
            {{ $t('settings.dataNormalization.module.infoModal.openDetails') }}
            <ReflectIconButton
              class="open-icon"
              icon="icon-open-external-2"
              size="small"
              theme="transparent"
            />
          </p>
        </div>
      </div>
      <div
        v-if="dataQualityDetail.sourceValueDimensions.length > 0"
        class="container__section"
      >
        <p class="section__title">
          <i class="title__icon icon-mapping" />
          {{ $t('settings.dataQuality.dataMapping') }}
        </p>
        <div
          v-for="sourceValueDimension in dataQualityDetail.sourceValueDimensions"
          :key="sourceValueDimension"
          class="section__field-container"
          @click="navigateToDataMapping(sourceValueDimension)"
        >
          <p class="field-container__info field-container__name">
            {{
              getSourceValueDimensionTranslation(sourceValueDimension, false)
            }}
          </p>
          <p class="field-container__info field-container__open-details">
            {{ $t('settings.dataMapping.module.infoModal.openDetails') }}
            <ReflectIconButton
              class="open-icon"
              icon="icon-open-external-2"
              size="small"
              theme="transparent"
            />
          </p>
        </div>
      </div>
    </div>
  </ReflectModal>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import { ROUTE } from '~/constants/routes'
import { getSourceValueDimensionTranslation } from '~/services/sourceValue'
import { useDataQualityStore } from '~/stores/dataQuality'
import {
  NormalizationCheckStatus,
  DataHealthCheckType,
  type DataQualityDetail
} from '~/types/dataQuality'
import { QUERY_PARAMETERS } from '~/types/queryParameters'
import type {
  NormalizedFieldEntry,
  SourceValueDimension
} from '~/types/sourceValue'
import type { DateRange } from '~/types/timeDimension'
import { buildObjectFromEntries, getObjectKeys } from '~/types/utils'

export default {
  name: 'DataQualityModal',
  props: {
    dateRange: {
      type: Object as PropType<DateRange>,
      required: true
    },
    dataQualityDetail: {
      type: Object as PropType<DataQualityDetail>,
      required: true
    }
  },
  emits: {
    'close-modal': () => true
  },
  setup() {
    return {
      NormalizationCheckStatus,
      DataHealthCheckType,
      getObjectKeys,
      getSourceValueDimensionTranslation
    }
  },
  computed: {
    ...mapState(useDataQualityStore, ['allDataHealthChecksMap']),
    dataCoherenceDetail() {
      return this.getDataHealthDetailByType(
        DataHealthCheckType.INCOHERENT_VALUES
      )
    },
    dataCompletenessDetail() {
      return this.getDataHealthDetailByType(DataHealthCheckType.NULL_VALUES)
    }
  },
  methods: {
    closeModal() {
      this.$emit('close-modal')
    },
    async navigateToNormalization(field: NormalizedFieldEntry) {
      await navigateTo(
        {
          path: ROUTE.SETTINGS__INTEGRATIONS__NORMALIZATION,
          query: {
            [QUERY_PARAMETERS.NORMALIZATION_FIELD]: field,
            [QUERY_PARAMETERS.START_DATE]: this.dateRange[0],
            [QUERY_PARAMETERS.END_DATE]: this.dateRange[1]
          }
        },
        { open: { target: '_blank' } }
      )
    },
    async navigateToDataMapping(sourceValueDimension: SourceValueDimension) {
      await navigateTo(
        {
          path: ROUTE.SETTINGS__INTEGRATIONS__MAPPING,
          query: {
            [QUERY_PARAMETERS.SOURCE_VALUE_DIMENSION]: sourceValueDimension,
            [QUERY_PARAMETERS.START_DATE]: this.dateRange[0],
            [QUERY_PARAMETERS.END_DATE]: this.dateRange[1]
          }
        },
        { open: { target: '_blank' } }
      )
    },
    getDataHealthDetailByType(type: DataHealthCheckType) {
      return buildObjectFromEntries(
        getObjectKeys(this.dataQualityDetail.dataHealthChecks)
          .filter(
            dataHealthCheck =>
              this.allDataHealthChecksMap[dataHealthCheck]?.type === type
          )
          .map(dataHealthCheck => [
            dataHealthCheck,
            this.dataQualityDetail.dataHealthChecks[dataHealthCheck]
          ])
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.container {
  width: 800px;
  height: 577px;

  &__section {
    display: flex;
    flex-direction: column;
    margin: $margin-medium 0;

    &:first-child {
      margin-top: 0;
    }

    .section {
      &__title {
        @include font-text($font-weight-medium);
        @include font-size($font-size-regular);
        padding: $margin-small 0 $margin-mini $margin-regular;
        background-color: $bg-quinary;
        border: 1px solid $border-quaternary;
        border-radius: 8px 8px 0 0;

        &__separator {
          width: 100%;
        }

        .title {
          &__icon {
            @include font-icon;
          }
        }
      }

      &__field-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        border: 1px solid $border-quaternary;
        border-top: none;

        .field-container {
          &__info {
            @include font-text($font-weight-book);
            @include font-size($font-size-mini);
            margin: $margin-small $margin-regular;
          }

          &__name {
            overflow: hidden;
            text-overflow: ellipsis;
            flex-basis: 50%;
          }

          &__status {
            width: 240px;
            display: flex;
            align-items: center;
            flex: 2;

            &__loader {
              position: relative;
              top: 0;
              left: 0;
              margin: 0;
              width: 24px;
              height: 24px;
            }

            &__icon {
              @include font-icon;
              @include font-size($font-size-regular);
              margin-right: $margin-tiny;
            }

            .status-icon {
              @include font-size($font-size-regular);

              &__success {
                font-size: 6px;
                background-color: $color-palette-14;
                color: $bg-quinary;
                padding: 3px;
                border-radius: 50%;
                margin-right: $margin-mini;
                margin-left: 2px;
              }

              &__error {
                color: $color-error;
              }
            }
          }

          &__open-details {
            display: flex;
            align-items: center;

            .open-icon {
              border: none;
            }
          }
        }

        &:last-of-type {
          margin-bottom: $margin-mini;
          border-radius: 0 0 8px 8px;
        }

        &:nth-child(odd) {
          background-color: $bg-quaternary;
        }

        &:hover {
          background-color: $bg-new-quaternary;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
